// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useCallback, useEffect, useState } from 'react';

import { displaySettingsAPI, type RelayStatus } from '../../../utils/apiClient';
import logger from '../../../utils/logger';

/** How often to refresh relay status while the settings panel is open. */
const POLL_MS = 30_000;

const CHANNEL_LABELS: Record<number, string> = {
  0: 'Off',
  1: 'Channel 1',
  2: 'Channel 2',
  3: 'Both channels',
};

const fmtAge = (ts: number | null): string => {
  if (!ts) return 'never';
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (secs < 90) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 90) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
};

interface RelayStatusPanelProps {
  /** Change this to force an immediate refetch (e.g. after the relay config changes). */
  refreshKey?: string | number;
}

/**
 * RelayStatusPanel — read-only liveness readout for the DNIL relay. The relay lives in
 * server_settings (not the device table), so this surfaces its enabled state, last-seen, and
 * online/offline (derived server-side from the same silence window the maintenance job uses).
 * Renders nothing until a relay devEUI is configured.
 */
const RelayStatusPanel: React.FC<RelayStatusPanelProps> = ({ refreshKey = 0 }) => {
  const [status, setStatus] = useState<RelayStatus | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await displaySettingsAPI.relayStatus();
      if (res?.success) setStatus(res.status);
    } catch (err) {
      logger.error('RelayStatusPanel: failed to load relay status', err);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load, refreshKey]);

  // Nothing configured yet → no panel (the config inputs above are the entry point).
  if (!status || !status.deveui) return null;

  const { enabled, online } = status;
  const dotColor = !enabled
    ? '#9e9e9e'
    : online === false
      ? '#e53935'
      : online
        ? '#43a047'
        : '#fb8c00';
  const stateLabel = !enabled
    ? 'Disabled'
    : online === false
      ? 'Offline (silent)'
      : online
        ? 'Online'
        : 'Awaiting first uplink';
  const thresholdHrs = Math.round((status.silenceThresholdMs / 3_600_000) * 10) / 10;

  return (
    <div
      data-testid="relay-status-panel"
      style={{
        marginTop: '0.8vmin',
        padding: '0.8vmin 1vmin',
        border: '1px solid #ddd',
        borderRadius: '0.6vmin',
        fontSize: '0.9em',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        rowGap: '0.3vmin',
        columnGap: '1vmin',
        alignItems: 'center',
        maxWidth: '28em',
      }}
    >
      <span
        style={{
          gridColumn: '1 / span 2',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5vmin',
          fontWeight: 600,
        }}
      >
        <span
          aria-hidden
          style={{
            width: '0.9em',
            height: '0.9em',
            borderRadius: '50%',
            background: dotColor,
            display: 'inline-block',
          }}
        />
        Relay status: {stateLabel}
      </span>

      <span style={{ color: '#666' }}>Enabled on alarm</span>
      <span>{CHANNEL_LABELS[status.channels] ?? `Mask ${status.channels}`}</span>

      <span style={{ color: '#666' }}>Last heard from</span>
      <span title={status.lastSeen ? new Date(status.lastSeen).toLocaleString() : undefined}>
        {fmtAge(status.lastSeen)}
      </span>

      <span style={{ color: '#666' }}>Last confirmed state</span>
      <span title={status.confirmedAt ? new Date(status.confirmedAt).toLocaleString() : undefined}>
        {fmtAge(status.confirmedAt)}
      </span>

      <span style={{ color: '#666' }}>Offline after</span>
      <span>{thresholdHrs}h of silence</span>
    </div>
  );
};

export default RelayStatusPanel;
